import { buildItemFromProduct } from "@/utils/bus/busAnalyticsUtils";

const formatTime = (dateString) => {
  try {
  if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch (error) {
    return dateString;
  }
};

const formatDate = (dateString) => {
  try {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return '';
  }
};

export const buildBusEcommerceItem = ({
  leg,
  index,
  summaryData,
  item_list_id,
  item_list_name,
  quantity = 1,
}) => {
  if (!leg) return [];

  const buildItem = (legLike, itemIndex) => ({
    item_id: `${legLike?.FirstStopLocation}-${legLike?.LastStopLocation}`,
    item_name: "bus",
    affiliation: "turna.com", // ya da Turna tarafındaki sayısal değerler
    coupon: legLike?.PaxPricesSummary?.AppliedCampaignCodes.length > 0 ? legLike?.PaxPricesSummary?.AppliedCampaignCodes?.join(",") : legLike?.PaxPricesSummary?.AppliedCampaignName?.join(",") || null,
    discount:
      (legLike?.PaxPricesSummary?.Total || 0) -
        (legLike?.PaxPricesSummary?.DiscountedTotal || 0) || 0,
    index: itemIndex,
    item_brand: legLike?.BusCompany || null,
    item_category: "bus",
    item_category2: legLike?.IsDomestic ? "domestic" : "international",
    origin: legLike?.FirstStopLocation || summaryData?.fromCity || null,
    destination: legLike?.LastStopLocation || summaryData?.toCity || null,
    departure_time: formatTime(legLike?.DepartureTime) || null,
    start_date: formatDate(legLike?.DepartureTime) || null,
    item_list_id,
    item_list_name,
    price: legLike?.PaxPricesSummary?.Total || 0,
    quantity: quantity || 1,
  });

  // Smart (aktarmalı) seferlerde her bir sub trip'i ayrı item olarak dön
  if (leg?.IsSmartTrip && Array.isArray(leg?.SubBusTrips) && leg.SubBusTrips.length > 0) {
    return leg.SubBusTrips.map((subTrip, subIndex) =>
      buildItem(subTrip, index + subIndex)
    );
  }

  // Diğer durumlarda tek item'lik array dön
  return [buildItem(leg, index)];
};

/**
 * Payment info'dan add_payment_info ecommerce objesi oluşturur.
 * begin_checkout ile aynı yapı: value = her şey dahil toplam fiyat, items = quantity, price (ham), discount.
 * @param {Object} params
 * @param {Object} params.paymentInfo - state.paymentInfo (Data.Products, TotalPriceSummary, basketId vb.)
 * @param {string} [params.selectedCoponCode]
 * @param {Object} [params.selectedInstalment] - SubTotal için
 * @param {string} [params.currency="TRY"]
 * @returns {Object|null} ecommerce objesi veya geçersizse null
 */
export const buildAddPaymentInfoEcommerce = ({
  paymentInfo,
  selectedCoponCode = "",
  selectedInstalment = null,
  currency = "TRY",
}) => {
  const products = paymentInfo?.Data?.Products ?? [];
  if (!Array.isArray(products) || products.length === 0) return null;

  const priceSummary =
    paymentInfo?.Data?.TotalPriceSummary?.PriceSummary ?? null;
  const baseTotalFare = Number(priceSummary?.TotalFare) ?? 0;
  const isInsuranceSelected = Boolean(priceSummary?.IsRefundInsuranceSelected);
  const insurancePrice = Number(priceSummary?.RefundInsurancePrice) ?? 0;
  const totalWithInsurance =
    Number(priceSummary?.TotalFareWithInsurance) ?? baseTotalFare + insurancePrice;
  const totalValue =
    selectedInstalment?.SubTotal ??
    paymentInfo?.StructuredInstallments?.SinglePayments?.[0]?.SubTotal ??
    (isInsuranceSelected ? totalWithInsurance : baseTotalFare);
  const currencyCode =
    priceSummary?.Currency ?? currency?.toUpperCase() ?? "TRY";
  const basketId = paymentInfo?.basketId;
  const hasBasketId =
    basketId != null &&
    String(basketId).trim() !== "" &&
    Number.isFinite(Number(basketId));
  const coupon =
    selectedCoponCode ||
    paymentInfo?.Data?.Basket?.UsedCampaign?.Code ||
    paymentInfo?.Data?.Basket?.UsedCampaign?.Title ||
    paymentInfo?.Data?.Basket?.UsedCampaign?.Name ||
    paymentInfo?.UsedCampaign?.CampaignCode ||
    "";

  const productCount = products.length;
  const pricePerItem =
    productCount > 0 ? Math.round(baseTotalFare / productCount) : baseTotalFare;

  const items = products.map((product, index) => {
    const tickets = product?.Tickets ?? [];
    const ticketCount = tickets.length || 1;
    const priceFromTickets =
      tickets.length > 0
        ? Math.round(
            tickets.reduce(
              (s, t) => s + (Number(t?.PriceSummary?.BaseFare) || 0),
              0
            ) / ticketCount
          )
        : pricePerItem;
    const discountFromTickets =
      tickets.length > 0
        ? tickets.reduce(
            (s, t) => s + (Number(t?.PriceSummary?.Discount) || 0),
            0
          )
        : 0;
    return buildItemFromProduct(product, index, {
      coupon,
      pricePerItem: priceFromTickets,
      quantity: ticketCount,
      discount: discountFromTickets,
      item_list_id: "Search_List",
      item_list_name: "Search List",
    });
  });

  const customerTotalFare = priceSummary?.CustomerTotalFare ?? totalValue ?? 0;

  if (!items.length) return null;

  return {
    currency: currencyCode,
    value: Number(customerTotalFare) || 0,
    coupon: coupon || "",
    ...(hasBasketId ? { basket_id: String(basketId) } : {}),
    items,
  };
};

/**
 * Rezervasyon sayfası begin_checkout ecommerce objesi oluşturur.
 * @param {Object} params
 * @param {Array} params.products - Basket'ten gelen ürünler
 * @param {number} [params.totalValue=0]
 * @param {string} [params.currency="TRY"]
 * @param {string|null} [params.basketId]
 * @param {Object} [params.basketData] - UsedCampaign için
 * @returns {Object|null} ecommerce objesi veya geçersizse null
 */
export const buildBeginCheckoutEcommerce = ({
  products = [],
  totalValue = 0,
  currency = "TRY",
  basketId = null,
  basketData = null,
}) => {
  if (!Array.isArray(products) || products.length === 0) return null;

  const productCount = products.length;
  const pricePerItem =
    productCount > 0 ? Math.round(totalValue / productCount) : totalValue;
  const coupon =
    basketData?.UsedCampaign?.CampaignCode ||
    basketData?.UsedCampaign?.Code ||
    basketData?.UsedCampaign?.Title ||
    basketData?.UsedCampaign?.Name ||
    null;

  const items = products.map((product, index) => {
    const tickets = product?.Tickets ?? [];
    const ticketCount = tickets.length || 1;
    const priceFromTickets =
      tickets.length > 0
        ? Math.round(
            tickets.reduce((s, t) => s + (Number(t?.PriceSummary?.BaseFare) || 0), 0) /
              ticketCount
          )
        : pricePerItem;
    const discountFromTickets =
      tickets.length > 0
        ? tickets.reduce((s, t) => s + (Number(t?.PriceSummary?.Discount) || 0), 0)
        : 0;
    return buildItemFromProduct(product, index, {
      coupon,
      pricePerItem: priceFromTickets,
      quantity: ticketCount,
      discount: discountFromTickets,
    });
  });

  return {
    currency: currency || "TRY",
    value: Number(totalValue) || 0,
    ...(coupon ? { coupon } : {}),
    ...(basketId != null && String(basketId).trim() !== ""
      ? { basket_id: String(basketId) }
      : {}),
    items,
  };
};

/**
 * Extra service (sigorta) ecommerce objesi oluşturur.
 * @param {Object} params
 * @param {boolean} params.isSelected - Checkbox seçili mi (eventAction belirler)
 * @param {number} [params.insurancePrice=0]
 * @param {string} [params.currency="TRY"]
 * @param {string|null} [params.basketId]
 * @returns {{ eventAction: string, ecommerce: Object }}
 */
export const buildExtraServiceEcommerce = ({
  isSelected,
  insurancePrice = 0,
  currency = "TRY",
  basketId = null,
}) => {
  const eventAction = isSelected ? "add_to_cart" : "remove_from_cart";
  const price = Number(insurancePrice) || 0;
  const resolvedCurrency = currency || "TRY";
  const hasBasketId =
    basketId != null &&
    String(basketId).trim() !== "" &&
    Number.isFinite(Number(basketId));

  const ecommerce = {
    currency: resolvedCurrency,
    value: price,
    ...(hasBasketId ? { basket_id: String(basketId) } : {}),
    items: [
      {
        item_name: "cancellation_service_bus",
        item_category: "extra service",
        price,
        quantity: 1,
        currency: resolvedCurrency,
      },
    ],
  };

  return { eventAction, ecommerce };
};

/**
 * Payment completed (purchase) ecommerce objesi oluşturur.
 * @param {Object} params
 * @param {Object} params.paymentInfo - state.paymentInfo
 * @param {Object} [params.cardProgram] - state.cardProgram (BIN detayları)
 * @param {Object} [params.creditCardPayment] - CreditCardPayments'ten gelen kart ödeme objesi (InstallmentCount, CardOrganizationType, BankName)
 * @param {string} [params.currency="TRY"]
 * @param {string} [params.selectedCoponCode] - Kampanya kodu override
 * @returns {Object|null} ecommerce objesi veya geçersizse null
 */
const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const buildPurchaseEcommerce = ({
  paymentInfo,
  cardProgram = null,
  currency = "TRY",
  selectedCoponCode = "",
  creditCardPayment = null,
  bankInfo = null,
}) => {
  const products = paymentInfo?.Data?.Products ?? [];
  if (!Array.isArray(products) || products.length === 0) return null;

  const priceSummary =
    paymentInfo?.Data?.TotalPriceSummary?.PriceSummary ?? null;
  const totalFare = toNumber(priceSummary?.TotalFare);
  const isInsuranceSelected = Boolean(priceSummary?.IsRefundInsuranceSelected);
  const insurancePrice = toNumber(priceSummary?.CustomerRefundInsurancePrice ?? priceSummary?.RefundInsurancePrice);
  const totalFareFromSummary =
    isInsuranceSelected
      ? toNumber(priceSummary?.TotalFareWithInsurance, totalFare + insurancePrice)
      : totalFare;

  const rawAllPayments =
    paymentInfo?.Data?.AllPayments ??
    paymentInfo?.Data?.Basket?.AllPayments ??
    [];
  const allPaymentsList = Array.isArray(rawAllPayments)
    ? (rawAllPayments.some((p) => p?.IsFinalized === true)
        ? rawAllPayments.filter((p) => p?.IsFinalized === true)
        : rawAllPayments)
    : [];

  const valueFromAllPayments =
    allPaymentsList.length > 0
      ? allPaymentsList
          .filter((p) => p?.PaymentType !== 2 && p?.PointAmount == null)
          .reduce(
            (sum, p) =>
              sum + toNumber(p?.CustomerTotalChargeAmount ?? p?.TotalChargeAmount ?? p?.ChargeAmount),
            0
          )
      : null;

  const value =
    valueFromAllPayments != null ? valueFromAllPayments : totalFareFromSummary;

  const currencyCode = paymentInfo?.Data?.CustomerCurrency ??
    priceSummary?.Currency ?? currency?.toUpperCase() ?? "TRY";

  const basketId =
    paymentInfo?.Data?.Basket?.BasketId ??
    paymentInfo?.Data?.BasketId ??
    paymentInfo?.basketId;
  const hasBasketId =
    basketId != null &&
    String(basketId).trim() !== "" &&
    Number.isFinite(Number(basketId));

  const coupon =
    selectedCoponCode ||
    paymentInfo?.Data?.Basket?.UsedCampaign?.Code ||
    paymentInfo?.Data?.Basket?.UsedCampaign?.Title ||
    paymentInfo?.Data?.Basket?.UsedCampaign?.Name ||
    paymentInfo?.UsedCampaign?.CampaignCode ||
    "";
  const couponUsed = coupon ? "yes" : undefined;

  const finalizedPayment =
    allPaymentsList.find((p) => p?.IsFinalized === true) ??
    allPaymentsList[0] ??
    rawAllPayments?.find((p) => p?.IsFinalized === true) ??
    rawAllPayments?.[0];

  const pointPaymentFromAllPayments =
    rawAllPayments?.find(
      (p) => p?.IsFinalized === true && p?.PaymentType === 2,
    )?.PointAmount;
  const pointPaymentFromLoyalty =
    paymentInfo?.Data?.LoyaltyPointPayments?.find(
      (p) => p?.IsFinalized === true,
    )?.PointAmount;
  /*const turnaPoint =
    paymentInfo?.UserPointDetail?.AppliedPointAmount != null
      ? Number(paymentInfo.UserPointDetail.AppliedPointAmount) *
        Number(paymentInfo.UserPointDetail.ConvertRate ?? 1)
      : pointPaymentFromAllPayments != null
        ? Number(pointPaymentFromAllPayments)
        : pointPaymentFromLoyalty != null
          ? Number(pointPaymentFromLoyalty)
          : undefined;*/

  const loyaltyPoint = paymentInfo?.Data?.LoyaltyPointPayments?.find((p) => p?.IsFinalized === true);
  const turnaPoint = loyaltyPoint?.CustomerAmount ?? loyaltyPoint?.PointAmount;

  const turnaPointAmount = toNumber(turnaPoint, 0);
  const finalValue = toNumber(value, 0) + turnaPointAmount;

  const hasTurnaPointPayment = allPaymentsList.some((p) => p?.PaymentType === 2);
  const hasCreditCardPayment = allPaymentsList.some((p) => p?.PaymentType === 0);
  const paymentType = hasTurnaPointPayment
    ? hasCreditCardPayment
      ? "credit card,turna point"
      : "turna point"
    : "credit card";
          
  const earnedTurnaPoint =
    paymentInfo?.Data?.TotalPriceSummary?.PriceSummary?.ServiceCharge ??
    paymentInfo?.UserPointDetail?.EarnedPointAmount ??
    paymentInfo?.Data?.EarnedPointAmount ??
    paymentInfo?.Data?.Basket?.EarnedPointAmount;

  const creditCardType =
    bankInfo?.VirtualPosName ??
    creditCardPayment?.CardOrganizationType ??
    cardProgram?.CardOrganizationType ??
    cardProgram?.CardType ??
    finalizedPayment?.CardOrganizationType ??
    finalizedPayment?.CardType;
  const creditCardBank =
    bankInfo?.BankName ??
    creditCardPayment?.BankName ??
    cardProgram?.BankName ??
    finalizedPayment?.BankName;
  const paymentInstallment =
    creditCardPayment?.InstallmentCount ?? finalizedPayment?.InstallmentCount;

  const productCount = products.length;
  const busTotal = totalFare;
  const pricePerItem =
    productCount > 0 ? Math.round(busTotal / productCount) : busTotal;

  const items = [];

  // 1. Sigorta item (seçiliyse)
  if (isInsuranceSelected && insurancePrice > 0) {
    items.push({
      item_name: "İptal Güvencesi",
      item_category: "extra service",
      price: insurancePrice,
      quantity: 1,
      currency: currencyCode,
    });
  }

  // 2. Bus items - begin_checkout/add_payment_info ile aynı: quantity, price (ham), discount
  products.forEach((product, index) => {
    const tickets = product?.Tickets ?? [];
    const ticketCount = tickets.length || 1;
    const priceFromTickets =
      tickets.length > 0
        ? Math.round(
            tickets.reduce(
              (s, t) => s + (Number(t?.PriceSummary?.CustomerBaseFare ?? t?.PriceSummary?.BaseFare) || 0),
              0
            ) / ticketCount
          )
        : pricePerItem;
    const discountFromTickets =
      tickets.length > 0
        ? tickets.reduce(
            (s, t) => s + (Number(t?.PriceSummary?.CustomerDiscount ?? t?.PriceSummary?.Discount) || 0),
            0
          )
        : 0;
    const ticketId =
      product?.Tickets?.[0]?.Id ??
      product?.Id ??
      product?.BookingCode;
    const item = buildItemFromProduct(product, index, {
      coupon,
      pricePerItem: priceFromTickets,
      quantity: ticketCount,
      discount: discountFromTickets,
      item_list_id: "Search_List",
      item_list_name: "Search List",
      ticketId: ticketId != null ? String(ticketId) : null,
    });
    items.push({...item, currency: currencyCode});
  });

  if (items.length === 0) return null;

  return {
    ...(hasBasketId && { transaction_id: String(basketId) }),
    value: finalValue,
    ...(hasBasketId ? { basket_id: String(basketId) } : {}),
    tax: 0,
    currency: currencyCode,
    ...(coupon ? { coupon } : {}),
    ...(couponUsed ? { coupon_used: couponUsed } : {}),
    transaction_category: "bus",
    payment_type: paymentType,
    credit_card_type: creditCardType
      ? String(creditCardType).trim().toLowerCase()
      : null,
    credit_card_bank: creditCardBank
      ? String(creditCardBank).trim().toLowerCase()
      : null,
    payment_installment:
      paymentInstallment != null ? Number(paymentInstallment) : null,
    sc_value: 0,
    turna_point: turnaPoint != null ? Number(turnaPoint) : null,
    earned_turna_point:
      earnedTurnaPoint != null ? Number(earnedTurnaPoint) : null,
    affiliation: "turna.com",
    items,
  };
};

/**
 * Refund (iptal) ecommerce objesi oluşturur.
 * Her iptal işleminde tetiklenir. Aynı transaction_id ile quantity iptal edilen bilet sayısını yansıtır.
 *
 * @param {Object} params
 * @param {Array} params.products - Basket Products
 * @param {Object} params.basketData - Data.Basket veya Data (BasketId, AllPayments, UsedCampaign)
 * @param {Object} params.priceSummary - TotalPriceSummary.PriceSummary veya Basket.PriceSummary
 * @param {number} params.cancelQuantity - İptal edilen bilet sayısı (quantity)
 * @param {string} [params.currency="TRY"]
 * @returns {Object|null} ecommerce objesi veya geçersizse null
 */
export const buildRefundEcommerce = ({
  products = [],
  basketData = null,
  priceSummary = null,
  cancelQuantity = 1,
  currency = "TRY",
}) => {
  if (!Array.isArray(products) || products.length === 0 || cancelQuantity < 1) {
    return null;
  }

  const totalFare = Number(priceSummary?.CustomerTotalFare ?? priceSummary?.TotalFare) ?? 0;
  const valueTotalFare =
    Number(priceSummary?.CustomerTotalFare ?? priceSummary?.TotalFare) || 0;
  const currencyCode =
    basketData?.CustomerCurrency ??
    priceSummary?.Currency ??
    currency?.toUpperCase() ??
    "TRY";

  const totalTicketCount = Math.max(
    1,
    products.reduce((sum, p) => {
      const tickets = p?.Tickets ?? [];
      return sum + (Array.isArray(tickets) ? tickets.length : 0);
    }, 0)
  );

  const pricePerTicket = Math.round(totalFare / totalTicketCount);
  const refundValue = valueTotalFare;

  const firstProduct = products[0];
  const lastProduct = products[products.length - 1] ?? firstProduct;
  const firstSegments = firstProduct?.Segments ?? [];
  const lastSegments = lastProduct?.Segments ?? [];
  const firstSegment = firstSegments[0] ?? {};
  const lastSegment =
    lastSegments[lastSegments.length - 1] ?? firstSegments[firstSegments.length - 1] ?? firstSegment;
  const origin = firstSegment?.Origin ?? "";
  const destination = lastSegment?.Destination ?? "";
  const itemId = `${origin}-${destination}`.replace(/\s+/g, "-") || "bus";

  const basket =
    basketData?.Basket ?? basketData ?? {};
  const basketId =
    basket.BasketId ?? basketData?.BasketId ?? basketData?.Basket?.BasketId;
  const hasBasketId =
    basketId != null &&
    String(basketId).trim() !== "" &&
    Number.isFinite(Number(basketId));

  const finalizedPayment =
    basket?.AllPayments?.find((p) => p?.IsFinalized === true) ?? basket?.AllPayments?.[0];
  const coupon =
    basket?.UsedCampaign?.Code ??
    basket?.UsedCampaign?.Title ??
    basket?.UsedCampaign?.Name ??
    "";

  const creditCardType = finalizedPayment?.CardOrganizationType;
  const creditCardBank = finalizedPayment?.BankName;
  const paymentInstallment = finalizedPayment?.InstallmentCount;
  const hasTurnaPointPayment =
    basket?.AllPayments?.some((p) => p?.PaymentType === 2) ?? false;
  const hasCreditCardPayment =
    basket?.AllPayments?.some((p) => p?.PaymentType === 0) ?? false;
  const paymentType = hasTurnaPointPayment
    ? hasCreditCardPayment
      ? "credit card,turna point"
      : "turna point"
    : "credit card";

  const item = {
    item_id: itemId,
    item_name: "bus",
    affiliation: "0",
    discount: 0,
    index: 0,
    item_brand: "turna",
    item_category: "bus",
    item_category2: firstSegment?.IsDomestic ? "domestic" : "international",
    price: pricePerTicket,
    quantity: cancelQuantity,
  };

  return {
    ...(hasBasketId && { transaction_id: String(basketId) }),
    value: refundValue,
    shipping: 0,
    tax: 0,
    currency: currencyCode,
    coupon: coupon || "",
    payment_type: paymentType,
    ...(creditCardType && { credit_card_type: creditCardType.toLowerCase() }),
    ...(creditCardBank && { credit_card_bank: creditCardBank }),
    ...(paymentInstallment != null && {
      payment_installment: Number(paymentInstallment),
    }),
    items: [item],
  };
};
